import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchGql } from '@/lib/graphql_client'
import { SupportedCountriesDocument } from '@/gql/graphql'

export interface CountryOption {
  value: string
  label: string
  description?: string
}

/**
 * The supported-countries list, fetched once and mapped once (W3.T3.5a).
 *
 * CountryCombobox and the template filter controls each ran this query themselves and each built
 * its own `{value,label}` array. That was not just duplication — they used the SAME query key
 * (`['supportedCountries']`) with DIFFERENT staleTime (Infinity vs 5 minutes), so which policy
 * applied depended on which component mounted first. One shared hook removes the race.
 *
 * `staleTime: Infinity` is the right answer of the two: the supported-country list does not change
 * within a session, and CountryCombobox already assumed it did not.
 *
 * NOT to be confused with the form-engine's PlacesSelect. This is form METADATA — the countries we
 * hold form templates for, returned as ISO codes alongside `supportedFormTypes(country_code)`.
 * PlacesSelect queries geography master data and returns place UUIDs. Which one a given country
 * field should use is an open data-model question (D4), so they stay separate until it is answered.
 */
export function useSupportedCountries(): {
  options: CountryOption[]
  isLoading: boolean
  isError: boolean
} {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['supportedCountries'],
    queryFn: ({ signal }) => fetchGql(SupportedCountriesDocument, {}, signal),
    staleTime: Infinity,
  })

  const options = useMemo<CountryOption[]>(
    () =>
      (data?.supportedCountries ?? [])
        .map((c) => ({
          value: c.code,
          label: c.flag_emoji ? `${c.flag_emoji} ${c.name}` : c.name,
          description: c.code,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [data?.supportedCountries],
  )

  return { options, isLoading, isError }
}
